/**
 * Stable, public editorial topic archives.
 *
 * Every established blog tag has one crawlable, self-canonical topic hub. The
 * fields below power both visible answer-first navigation and CollectionPage
 * metadata, so the topic taxonomy does not depend on JavaScript-only filters.
 */
export const BLOG_TOPIC_ARCHIVES = [
  {
    slug: "aeo-ai-search",
    tag: "AEO & AI Search",
    name: "AEO & AI Search",
    title: "AEO & AI Search Articles | Integrated.Social",
    description:
      "Evidence-led articles on answer engine optimisation, AI search, AI citations and practical visibility strategy for B2B teams.",
    answer:
      "This topic hub brings together practical, evidence-led analysis for teams assessing how search, answer engines and AI assistants affect discoverability, buyer research and measurement.",
    service: {
      href: "/services/seo-aeo-geo/",
      label: "SEO, AEO & GEO services",
    },
  },
  {
    slug: "ai-marketing-strategy",
    tag: "AI Marketing Strategy",
    name: "AI Marketing Strategy",
    title: "AI Marketing Strategy Articles | Integrated.Social",
    description:
      "Practical analysis of AI marketing strategy, operating models, buyer journeys and accountable B2B growth.",
    answer:
      "This topic hub covers how marketing leaders can turn AI capabilities into governed operating choices, clearer buyer journeys and measurable commercial work rather than isolated tool experiments.",
    service: {
      href: "/services/ai-marketing-strategy/",
      label: "AI marketing strategy services",
    },
  },
  {
    slug: "agentic-ai",
    tag: "Agentic AI",
    name: "Agentic AI",
    title: "Agentic AI Articles | Integrated.Social",
    description:
      "Evidence-led articles on agentic AI, AI agents, workflows and their implications for marketing and commercial operations.",
    answer:
      "This topic hub explains where AI agents can support marketing and commercial workflows, the controls they need and the questions leaders should answer before operational use.",
    service: {
      href: "/services/gemini-agentic-ai/",
      label: "Agentic AI services",
    },
  },
  {
    slug: "ai-governance-safety",
    tag: "AI Governance & Safety",
    name: "AI Governance & Safety",
    title: "AI Governance & Safety Articles | Integrated.Social",
    description:
      "Articles on AI governance, safety, security, oversight and responsible deployment for decision-makers.",
    answer:
      "This topic hub helps decision-makers evaluate AI governance, safety, security and oversight requirements alongside the commercial use cases that make those controls necessary.",
    service: {
      href: "/services/ai-governance/",
      label: "AI governance services",
    },
  },
  {
    slug: "ppc-performance-max",
    tag: "PPC & Performance Max",
    name: "PPC & Performance Max",
    title: "PPC & Performance Max Articles | Integrated.Social",
    description:
      "Practical PPC and Performance Max analysis for B2B marketers, from measurement and bidding to AI search change.",
    answer:
      "This topic hub examines paid search, Performance Max, measurement and AI-driven changes in how B2B teams plan, govern and improve media investment.",
    service: {
      href: "/services/ppc-performance-max/",
      label: "PPC & Performance Max services",
    },
  },
  {
    slug: "ai-news-trends",
    tag: "AI News & Trends",
    name: "AI News & Trends",
    title: "AI News & Trends | Integrated.Social",
    description:
      "Source-qualified analysis of AI news, infrastructure, platforms and market developments that matter to marketers.",
    answer:
      "This topic hub separates confirmed AI market developments from speculation and explains the strategic questions marketers should ask as platforms, infrastructure and funding evolve.",
    service: {
      href: "/services/ai-marketing-strategy/",
      label: "AI marketing strategy services",
    },
  },
  {
    slug: "digital-marketing-tips",
    tag: "Digital Marketing Tips",
    name: "Digital Marketing Tips",
    title: "Digital Marketing Tips | Integrated.Social",
    description:
      "Actionable digital marketing guidance on content, demand generation, conversion and practical AI adoption.",
    answer:
      "This topic hub offers practical guidance on content, demand generation, conversion and responsible AI adoption for marketers improving an existing digital growth programme.",
    service: {
      href: "/services/ai-websites/",
      label: "AI website services",
    },
  },
] as const;

export type BlogTopicArchive = (typeof BLOG_TOPIC_ARCHIVES)[number];

export function getBlogTopicArchiveBySlug(slug: string | null | undefined): BlogTopicArchive | undefined {
  return BLOG_TOPIC_ARCHIVES.find((archive) => archive.slug === slug);
}

export function getBlogTopicArchiveByTag(tag: string | null | undefined): BlogTopicArchive | undefined {
  return BLOG_TOPIC_ARCHIVES.find((archive) => archive.tag === tag);
}

export function blogTopicArchivePath(archive: Pick<BlogTopicArchive, "slug">): string {
  return `/blog/topics/${archive.slug}/`;
}
